// cdn host
import { cdnHosts } from './constants.js';
import { config } from '@shakkerai/config';

/**
 * convert2CompressedUrl
 * @param {*} url
 * @param {*} width
 * @returns
 */
export function convert2CompressedUrl(url, width = 600) {
  // logger.info('convert2CompressedUrl', 'url', url);
  // logger.info('convert2CompressedUrl', 'width', width);

  // check
  if (!url) return;

  // format
  let format = 'webp';
  if (/\.(gif)(\?.*)?$/g.test(url)) return url;
  // logger.info('convert2CompressedUrl', 'format', format);

  // return
  return compressByFormatAndResizeWidth(url, width, format);
}

// compressByFormatAndResizeWidth
function compressByFormatAndResizeWidth(url, width, format = 'webp') {
  // logger.info('compressByFormatAndResizeWidth');

  // if (isPureImageUrl(url) && awsCdnHost.some((item) => url.includes(item))) {
  //   return url + `?handler=yes&width=${width}`;
  // }

  // check
  if (!isCDNImage(url) || !isPureImageUrl(url)) return url || '';

  // return
  const theWidth = Math.round(width);
  const finalUrl = `${url}${
    url?.indexOf('?') > -1 ? '&' : '?'
  }x-oss-process=image/resize,w_${theWidth},m_lfit/format,${format}`;
  // logger.info('compressByFormatAndResizeWidth', 'theWidth', theWidth);
  // logger.info('compressByFormatAndResizeWidth', 'finalUrl', finalUrl);
  return finalUrl;
}

// is cdn image
function isCDNImage(url) {
  // check
  if (!url) return;

  return cdnHosts.some((item) => {
    return url.includes(item);
  });
}

// is pure image
// https://help.aliyun.com/zh/oss/user-guide/latest-version-of-img-guide/?spm=a2c4g.11186623.0.0.4f824ebbhvTlNx
function isPureImageUrl(url) {
  if (!url || !url.startsWith('http')) return;
  const normalize = new URL(url);
  return normalize.search && /x-oss-process/.test(normalize.search) ? false : true;
}

export function getImageOssKey(url) {
  if (!url) return;
  if (!url.startsWith('http')) return url;

  return url.split('/').pop().split('.').shift();
}

export function getImageOssPath(url) {
  if (!url) return;
  if (!url.startsWith('http')) return url;

  return new URL(url).pathname.replace(/^\//, '');
}

export function getImageOssUrl(url) {
  if (!url) return;
  if (!url.startsWith('http')) return url;

  return new URL(url).origin + new URL(url).pathname;
}

export function getLocalImageSize(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
      });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = (error) => {
      console.error('Failed to get locale image size', error);
      reject('Failed to get locale image size');
    };
    img.src = URL.createObjectURL(file);
  });
}

export function getRemoteImageSize(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
      });
    };
    img.onerror = (error) => {
      console.error('Failed to get remote image size', error);
      reject('Failed to get remote image size');
    };
    img.src = url;
  });
}

export function getCDNImage(path, isSDTemp = false) {
  if (path?.startsWith('http')) return path;
  if (config.env === 'aws') {
    return (isSDTemp ? config.s3.cdnHostForSDTmp : config.s3.cdnHost) + '/' + path;
  } else {
    return (isSDTemp ? config.oss.cdnHostForSDTmp : config.oss.cdnHost) + '/' + path;
  }
}

export function fitImageSize(width, height, maxWidth, maxHeight) {
  const scale = Math.min(maxWidth / width, maxHeight / height);

  return {
    width: width * scale,
    height: height * scale,
  };
}

export async function resizeImage(img, targetWidth, targetHeight) {
  if (img.width === targetWidth && img.height === targetHeight) {
    return img;
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  context.drawImage(img, 0, 0, targetWidth, targetHeight);

  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.src = canvas.toDataURL(img.type, 1);
  });
}

export async function convertRemoteImageToBase64(url, maxWidth, maxHeight, format = 'image/jpeg', quality = 1) {
  const image = new Image();
  image.crossOrigin = 'Anonymous';
  image.src = url;

  await new Promise((resolve) => (image.onload = resolve));

  const { width, height } = image;
  const scale = Math.min(maxWidth / width, maxHeight / height);

  if (scale >= 1) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.width = width;
    canvas.height = height;

    context.drawImage(image, 0, 0, width, height);

    return canvas.toDataURL(format, quality);
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  const compressedWidth = width * scale;
  const compressedHeight = height * scale;

  canvas.width = compressedWidth;
  canvas.height = compressedHeight;
  const offsetX = 0;
  const offsetY = 0;

  // canvas.width = width;
  // canvas.height = height;
  // const offsetX = (maxWidth - compressedWidth) / 2;
  // const offsetY = (maxHeight - compressedHeight) / 2;

  context.drawImage(image, 0, 0, width, height, offsetX, offsetY, compressedWidth, compressedHeight);

  return canvas.toDataURL(format, quality);
}

export async function compressLocalImage(file, maxWidth, maxHeight) {
  const image = new Image();

  image.src = URL.createObjectURL(file);
  await new Promise((resolve) => (image.onload = resolve));

  const { width, height } = image;
  const scale = Math.min(maxWidth / width, maxHeight / height);

  if (scale >= 1) {
    return {
      file: file,
      originalWidth: width,
      originalHight: height,
      width: width,
      height: height,
      compressed: false,
    };
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  canvas.width = width * scale;
  canvas.height = height * scale;

  context.drawImage(image, 0, 0, width, height, 0, 0, width * scale, height * scale);

  const base64 = canvas.toDataURL(file.type, 1);
  const blob = await fetch(base64).then((res) => res.blob());

  return {
    file: new File([blob], file.name, { type: file.type }),
    originalWidth: width,
    originalHight: height,
    width: canvas.width,
    height: canvas.height,
    compressed: true,
  };
}
